import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Card,
  Tag,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Tooltip,
  Row,
  Col,
  Statistic,
  Select,
} from "antd";
import {
  EyeOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  PlusOutlined,
  PoundOutlined,
  HistoryOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  loadAllLoanRequests,
  approveLoanRequest,
  rejectLoanRequest,
} from "../../redux/rtk/features/loanRequest/loanRequestSlice";
import {
  getAllLoan,
  addEMI,
  getLoanEMIHistory,
  updateLoan,
  deleteLoanEMI,
} from "../../redux/rtk/features/loan/loanSlice";
import moment from "moment";

const { TextArea } = Input;

const ManageLoans = () => {
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [emiModalVisible, setEmiModalVisible] = useState(false);
  const [emiHistoryModalVisible, setEmiHistoryModalVisible] = useState(false);
  const [requestDetailsModalVisible, setRequestDetailsModalVisible] =
    useState(false);
  const [editLoanModalVisible, setEditLoanModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [approvalForm] = Form.useForm();
  const [rejectionForm] = Form.useForm();
  const [emiForm] = Form.useForm();
  const [editLoanForm] = Form.useForm();
  const [previewBalance, setPreviewBalance] = useState(0);

  const dispatch = useDispatch();
  const { list: loanRequests, loading: loanRequestLoading } = useSelector(
    (state) => state.loanRequest
  );
  const {
    list: loans,
    loading: loanLoading,
    emiHistory,
  } = useSelector((state) => state.loan);

  const loading = loanRequestLoading || loanLoading;

  useEffect(() => {
    fetchData();
  }, [dispatch]);

  const fetchData = () => {
    // Fetch all loan requests
    dispatch(loadAllLoanRequests());

    // Fetch all loans
    dispatch(getAllLoan());
  };

  const handleApprove = async (values) => {
    try {
      const response = await dispatch(
        approveLoanRequest({
          id: selectedRequest.id,
          values,
        })
      );

      if (response.payload?.message === "success") {
        setApprovalModalVisible(false);
        approvalForm.resetFields();
        setSelectedRequest(null);
        fetchData(); // Refresh the data
      }
    } catch (error) {
      console.error("Error approving loan request:", error);
    }
  };

  const handleReject = async (values) => {
    try {
      const response = await dispatch(
        rejectLoanRequest({
          id: selectedRequest.id,
          values,
        })
      );

      if (response.payload?.message === "success") {
        setRejectionModalVisible(false);
        rejectionForm.resetFields();
        setSelectedRequest(null);
        fetchData(); // Refresh the data
      }
    } catch (error) {
      console.error("Error rejecting loan request:", error);
    }
  };

  const handleEmiSubmit = async (values) => {
    try {
      const emiData = {
        loanId: selectedLoan.id,
        emi: values.emiAmount,
        currentDate: values.emiDate,
      };

      console.log("Recording EMI:", emiData);
      const response = await dispatch(addEMI(emiData));
      console.log("EMI response:", response);

      if (response.payload?.message === "success") {
        message.success("EMI recorded successfully!");
        setEmiModalVisible(false);
        emiForm.resetFields();
        setSelectedLoan(null);

        // Refresh the data to show updated balance
        console.log("Refreshing data after EMI recording...");
        await dispatch(getAllLoan()); // Explicitly refresh loan data
        message.success("Balance updated successfully!");
      } else {
        // message.error("Failed to record EMI. Please try again.");
      }
    } catch (error) {
      console.error("Error recording EMI:", error);
      // message.error("Error recording EMI. Please try again.");
    }
  };

  const showApprovalModal = (record) => {
    setSelectedRequest(record);
    setApprovalModalVisible(true);

    // Auto-fill the form with loan request data
    approvalForm.setFieldsValue({
      loanAmount: record.requestedAmount,
      totalMonthReturn: record.returningMonths,
    });
  };

  const showRejectionModal = (record) => {
    setSelectedRequest(record);
    setRejectionModalVisible(true);
  };

  const showEmiModal = (record) => {
    setSelectedLoan(record);
    setEmiModalVisible(true);
  };

  const showEmiHistoryModal = async (record) => {
    setSelectedLoan(record);
    setEmiHistoryModalVisible(true);

    console.log("Fetching EMI history for loan ID:", record.id);

    // Fetch EMI history using Redux action
    dispatch(getLoanEMIHistory(record.id));
  };

  const showEditLoanModal = (record) => {
    setSelectedLoan(record);
    setEditLoanModalVisible(true);

    // Pre-fill the form with current loan data
    editLoanForm.setFieldsValue({
      loanAmount: record.loanAmount,
      totalMonthReturn: record.totalMonthReturn,
      loanType: record.loanType,
    });

    // Set initial preview balance
    const totalEmiPaid = record.loanEMI
      ? record.loanEMI.reduce((sum, emi) => sum + (emi.EmiAmount || 0), 0)
      : 0;
    setPreviewBalance(record.loanAmount - totalEmiPaid);
  };

  const calculatePreviewBalance = useCallback(
    (newAmount) => {
      if (!selectedLoan) return 0;
      const totalEmiPaid = selectedLoan.loanEMI
        ? selectedLoan.loanEMI.reduce(
            (sum, emi) => sum + (emi.EmiAmount || 0),
            0
          )
        : 0;
      return newAmount - totalEmiPaid;
    },
    [selectedLoan]
  );

  // Debounced function to update preview balance
  const updatePreviewBalance = useCallback(
    (value) => {
      if (value && !isNaN(value)) {
        setPreviewBalance(calculatePreviewBalance(value));
      }
    },
    [calculatePreviewBalance]
  );

  const handleEditLoan = async (values) => {
    try {
      const originalAmount = selectedLoan.loanAmount;
      const newAmount = values.loanAmount;

      // Calculate total EMI payments made so far
      const totalEmiPaid = selectedLoan.loanEMI
        ? selectedLoan.loanEMI.reduce(
            (sum, emi) => sum + (emi.EmiAmount || 0),
            0
          )
        : 0;

      // Calculate new balance: new loan amount minus total EMI payments made
      const newBalance = newAmount - totalEmiPaid;

      console.log("Balance calculation:", {
        originalAmount,
        newAmount,
        totalEmiPaid,
        currentBalance: selectedLoan.balance,
        newBalance,
      });

      const updateData = {
        loanAmount: newAmount,
        totalMonthReturn: values.totalMonthReturn,
        loanType: values.loanType === "true" || values.loanType === true,
        balance: newBalance,
      };

      console.log("Frontend sending update data:", {
        loanId: selectedLoan.id,
        updateData,
        totalEmiPaid,
        originalAmount,
        newAmount,
      });

      const response = await dispatch(
        updateLoan({
          id: selectedLoan.id,
          values: updateData,
        })
      );

      if (response.payload?.message === "success" || response.payload) {
        // message.success("Loan updated successfully!");
        setEditLoanModalVisible(false);
        editLoanForm.resetFields();
        setSelectedLoan(null);
        fetchData(); // Refresh the data
      } else {
        // message.error("Failed to update loan. Please try again.");
      }
    } catch (error) {
      console.error("Error updating loan:", error);
      // message.error("Error updating loan. Please try again.");
    }
  };

  const handleDeleteEMI = async (emiId) => {
    try {
      const response = await dispatch(
        deleteLoanEMI({
          loanId: selectedLoan.id,
          emiId: emiId,
        })
      );

      if (response.payload?.message === "success") {
        message.success("EMI entry deleted successfully!");
        
        // Refresh EMI history
        dispatch(getLoanEMIHistory(selectedLoan.id));
        
        // Refresh loan data to update balance
        dispatch(getAllLoan());
      }
    } catch (error) {
      console.error("Error deleting EMI:", error);
      message.error("Error deleting EMI entry. Please try again.");
    }
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: "orange", text: "Pending" },
      approved: { color: "green", text: "Approved" },
      rejected: { color: "red", text: "Rejected" },
    };

    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const requestColumns = [
    {
      title: "Employee",
      dataIndex: "user",
      key: "user",
      render: (user) => (user ? `${user.firstName} ${user.lastName}` : "N/A"),
    },
    {
      title: "Transaction Type",
      dataIndex: "loanType",
      key: "loanType",
      render: (loanType) => (
        <Tag color={loanType ? "blue" : "green"}>
          {loanType ? "Advance" : "Loan"}
        </Tag>
      ),
    },
    {
      title: "Amount (£)",
      dataIndex: "requestedAmount",
      key: "requestedAmount",
      render: (amount) => `£${amount?.toLocaleString()}`,
    },
    {
      title: "Purpose",
      dataIndex: "purpose",
      key: "purpose",
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: "Returning Months",
      dataIndex: "returningMonths",
      key: "returningMonths",
      render: (months) => (months ? `${months} months` : "N/A"),
    },
    {
      title: "Requested Date",
      dataIndex: "requestedDate",
      key: "requestedDate",
      render: (date) => moment(date).format("DD/MM/YYYY"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => {
        if (record.status === "pending") {
          return (
            <Space size="small">
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => showApprovalModal(record)}
              >
                {/* Approve */}
              </Button>
              <Button
                danger
                size="small"
                icon={<CloseOutlined />}
                onClick={() => showRejectionModal(record)}
              >
                {/* Reject */}
              </Button>
            </Space>
          );
        }

        return (
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedRequest(record);
              setRequestDetailsModalVisible(true);
            }}
          >
            View
          </Button>
        );
      },
    },
  ];

  const loanColumns = [
    {
      title: "Employee",
      dataIndex: "user",
      key: "user",
      render: (user) => (user ? `${user.firstName} ${user.lastName}` : "N/A"),
    },
    // {
    //   title: "Loan ID",
    //   dataIndex: "id",
    //   key: "id",
    // },
    {
      title: "Transaction Type",
      dataIndex: "loanType",
      key: "loanType",
      render: (loanType) => (
        <Tag color={loanType ? "blue" : "green"}>
          {loanType ? "Advance" : "Loan"}
        </Tag>
      ),
    },
    {
      title: "Amount (£)",
      dataIndex: "loanAmount",
      key: "loanAmount",
      render: (amount) => `£${amount?.toLocaleString()}`,
    },
    {
      title: "Returning Months",
      dataIndex: "totalMonthReturn",
      key: "totalMonthReturn",
      render: (months) => (months ? `${months} months` : "N/A"),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => moment(date).format("DD/MM/YYYY"),
    },
    {
      title: "Balance (£)",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => `£${balance?.toLocaleString()}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<PoundOutlined />}
            onClick={() => showEmiModal(record)}
            disabled={!record.status || record.balance <= 0}
          >
            Record EMI
          </Button>
          <Button
            type="default"
            icon={<HistoryOutlined />}
            onClick={() => showEmiHistoryModal(record)}
            disabled={!record.status}
          >
            EMI History
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => showEditLoanModal(record)}
            disabled={!record.status}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  const pendingRequests = Array.isArray(loanRequests)
    ? loanRequests.filter((req) => req.status === "pending").length
    : 0;
  const activeLoans = Array.isArray(loans)
    ? loans.filter((loan) => loan.status).length
    : 0;
  const totalLoanAmount = Array.isArray(loans)
    ? loans.reduce((sum, loan) => sum + (loan.loanAmount || 0), 0)
    : 0;
  const totalOutstanding = Array.isArray(loans)
    ? loans.reduce((sum, loan) => sum + (loan.balance || 0), 0)
    : 0;

  return (
    <div>
      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: "20px" }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Requests"
              value={pendingRequests}
              valueStyle={{ color: "#fa8c16" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Advance"
              value={activeLoans}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Loan Amount"
              value={totalLoanAmount}
              precision={2}
              prefix="£"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Outstanding Balance"
              value={totalOutstanding}
              precision={2}
              prefix="£"
              valueStyle={{ color: "#f5222d" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Loan Requests Table */}
      <Card
        title="Loan Requests"
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            Refresh
          </Button>
        }
        style={{ marginBottom: "20px" }}
      >
        <Table
          columns={requestColumns}
          dataSource={Array.isArray(loanRequests) ? loanRequests : []}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Card>

      {/* Active Loans Table */}
      <Card title="Active Advance">
        <Table
          columns={loanColumns}
          dataSource={Array.isArray(loans) ? loans : []}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Card>

      {/* Approval Modal */}
      <Modal
        title="Approve Loan Request"
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          approvalForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={approvalForm} layout="vertical" onFinish={handleApprove}>
          <Form.Item
            label="Loan Amount (£)"
            name="loanAmount"
            rules={[{ required: true, message: "Please enter loan amount!" }]}
          >
            <InputNumber
              placeholder="Enter loan amount"
              style={{ width: "100%" }}
              min={1}
              formatter={(value) => {
                if (!value) return "";
                return `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              }}
              parser={(value) => {
                if (!value) return "";
                return value.replace(/\£\s?|(,*)/g, "");
              }}
              controls={false}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="Total Months for Return"
            name="totalMonthReturn"
            rules={[{ required: true, message: "Please enter total months!" }]}
          >
            <InputNumber
              placeholder="Enter total months"
              style={{ width: "100%" }}
              min={1}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Approve Loan
              </Button>
              <Button onClick={() => setApprovalModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Rejection Modal */}
      <Modal
        title="Reject Loan Request"
        open={rejectionModalVisible}
        onCancel={() => setRejectionModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={rejectionForm} layout="vertical" onFinish={handleReject}>
          <Form.Item
            label="Rejection Reason"
            name="rejectionReason"
            rules={[
              { required: true, message: "Please enter rejection reason!" },
            ]}
          >
            <TextArea
              placeholder="Please provide a reason for rejection..."
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" danger htmlType="submit">
                Reject Loan
              </Button>
              <Button onClick={() => setRejectionModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* EMI Recording Modal */}
      <Modal
        title="Record EMI Payment"
        open={emiModalVisible}
        onCancel={() => setEmiModalVisible(false)}
        footer={null}
        width={500}
      >
        <Form form={emiForm} layout="vertical" onFinish={handleEmiSubmit}>
          <Form.Item
            label="EMI Amount (£)"
            name="emiAmount"
            rules={[{ required: true, message: "Please enter EMI amount!" }]}
          >
            <InputNumber
              placeholder="Enter EMI amount"
              style={{ width: "100%" }}
              min={1}
              // formatter={(value) => {
              //   if (!value) return "";
              //   return `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              // }}
              // parser={(value) => {
              //   if (!value) return "";
              //   return value.replace(/\£\s?|(,*)/g, "");
              // }}
              controls={false}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="EMI Date"
            name="emiDate"
            rules={[{ required: true, message: "Please select EMI date!" }]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Record EMI
              </Button>
              <Button onClick={() => setEmiModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* EMI History Modal */}
      <Modal
        title={`EMI History - Loan ID: ${selectedLoan?.id}`}
        open={emiHistoryModalVisible}
        onCancel={() => {
          setEmiHistoryModalVisible(false);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setEmiHistoryModalVisible(false);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        <Table
          columns={[
            {
              title: "EMI Amount (£)",
              dataIndex: "EmiAmount",
              key: "EmiAmount",
              render: (amount) => `£${amount?.toLocaleString()}`,
            },
            {
              title: "EMI Date",
              dataIndex: "EmiDate",
              key: "EmiDate",
              render: (date) => moment(date).format("DD/MM/YYYY"),
            },
            {
              title: "Balance (£)",
              dataIndex: "balance",
              key: "balance",
              render: (balance) => `£${balance?.toLocaleString()}`,
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (status) => (
                <Tag color={status ? "green" : "red"}>
                  {status ? "Paid" : "Pending"}
                </Tag>
              ),
            },
            {
              title: "Actions",
              key: "actions",
              width: 100,
              render: (_, record) => (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    Modal.confirm({
                      title: "Delete EMI Entry",
                      content: `Are you sure you want to delete this EMI entry of £${record.EmiAmount?.toLocaleString()}? This will add the amount back to the loan balance.`,
                      okText: "Yes, Delete",
                      okType: "danger",
                      cancelText: "Cancel",
                      onOk() {
                        handleDeleteEMI(record.id);
                      },
                    });
                  }}
                  size="small"
                />
              ),
            },
          ]}
          dataSource={emiHistory}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} EMI records`,
          }}
          locale={{
            emptyText: "No EMI records found for this loan",
          }}
        />
      </Modal>

      {/* Request Details Modal */}
      <Modal
        title="Loan Request Details"
        open={requestDetailsModalVisible}
        onCancel={() => {
          setRequestDetailsModalVisible(false);
          setSelectedRequest(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setRequestDetailsModalVisible(false);
              setSelectedRequest(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={600}
      >
        {selectedRequest && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Employee:</strong>{" "}
              {selectedRequest.user
                ? `${selectedRequest.user.firstName} ${selectedRequest.user.lastName}`
                : "N/A"}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Requested Amount:</strong> £
              {selectedRequest.requestedAmount?.toLocaleString()}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Purpose:</strong> {selectedRequest.purpose}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Returning Months:</strong>{" "}
              {selectedRequest.returningMonths} months
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Requested Date:</strong>{" "}
              {moment(selectedRequest.requestedDate).format("DD/MM/YYYY")}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Status:</strong> {getStatusTag(selectedRequest.status)}
            </div>
            {selectedRequest.approvedDate && (
              <div style={{ marginBottom: 16 }}>
                <strong>Approved Date:</strong>{" "}
                {moment(selectedRequest.approvedDate).format("DD/MM/YYYY")}
              </div>
            )}
            {selectedRequest.rejectionReason && (
              <div style={{ marginBottom: 16 }}>
                <strong>Rejection Reason:</strong>{" "}
                {selectedRequest.rejectionReason}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Edit Loan Modal */}
      <Modal
        title="Edit Loan"
        open={editLoanModalVisible}
        onCancel={() => {
          setEditLoanModalVisible(false);
          editLoanForm.resetFields();
          setSelectedLoan(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={editLoanForm}
          layout="vertical"
          onFinish={handleEditLoan}
          // onValuesChange={(changedValues, allValues) => {
          //     if (changedValues.loanAmount && !isNaN(changedValues.loanAmount)) {
          //         updatePreviewBalance(changedValues.loanAmount);
          //     }
          // }}
          onValuesChange={(changedValues, allValues) => {
            if (changedValues.loanAmount && !isNaN(changedValues.loanAmount)) {
              updatePreviewBalance(changedValues.loanAmount);
            }
          }}
        >
          <Form.Item
            label="Transaction Type"
            name="loanType"
            rules={[
              { required: true, message: "Please select transaction type!" },
            ]}
          >
            <Select
              placeholder="Select transaction type"
              style={{ width: "100%" }}
            >
              <Select.Option value={false}>Loan</Select.Option>
              <Select.Option value={true}>Advance</Select.Option>
            </Select>
          </Form.Item>

          {/* <Form.Item
                        label="Loan Amount (£)"
                        name="loanAmount"
                        rules={[{ required: true, message: 'Please enter loan amount!' }]}
                    >
                        <InputNumber
                            placeholder="Enter loan amount"
                            style={{ width: '100%' }}
                            min={1}
                            formatter={value => {
                                if (!value) return '';
                                return `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                            }}
                            // parser={value => {
                            //     if (!value) return '';
                            //     return value.replace(/\£\s?|(,*)/g, '');
                            // }}
                            parser={value => value.replace(/\£\s?|(,*)/g, '')}

                            controls
                            precision={2}
                        />
                    </Form.Item> */}

          <Form.Item
            label="Loan Amount (£)"
            name="loanAmount"
            rules={[{ required: true, message: "Please enter loan amount!" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              // step={100}
              controls // show increment/decrement buttons
              formatter={(value) =>
                value ? `£ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
              }
              parser={(value) => (value ? value.replace(/£\s?|(,*)/g, "") : "")}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="Total Months for Return"
            name="totalMonthReturn"
            rules={[{ required: true, message: "Please enter total months!" }]}
          >
            <InputNumber
              placeholder="Enter total months"
              style={{ width: "100%" }}
              min={1}
            />
          </Form.Item>

          {selectedLoan && (
            <div
              style={{
                marginBottom: 16,
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <strong>Current Balance:</strong> £
                {selectedLoan.balance?.toLocaleString()}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>Total EMI Paid:</strong> £
                {selectedLoan.loanEMI
                  ? selectedLoan.loanEMI
                      .reduce((sum, emi) => sum + (emi.EmiAmount || 0), 0)
                      .toLocaleString()
                  : "0"}
              </div>
              <div style={{ marginBottom: 8 }}>
                <strong>New Balance (Preview):</strong> £
                {previewBalance.toLocaleString()}
              </div>
              {/* <div style={{ fontSize: '12px', color: '#666' }}>
                                Note: New balance = New loan amount - Total EMI payments made
                            </div> */}
            </div>
          )}

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update Loan
              </Button>
              <Button
                onClick={() => {
                  setEditLoanModalVisible(false);
                  editLoanForm.resetFields();
                  setSelectedLoan(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageLoans;
